if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.addEventListener('change', event => {
          if (event.target.matches('[data-ls-card-variant]')) this.syncCardVariant();
        });
        this.syncCardVariant();
        this.setupCardPicker();
      }

      setupCardPicker() {
        const select = this.querySelector('[data-ls-card-variant]');
        if (!select) return;
        const trigger = document.createElement('button');
        trigger.type = 'button'; trigger.className = 'ls-card-picker-trigger';
        trigger.id = `${select.id}-trigger`;
        trigger.setAttribute('aria-haspopup', 'listbox'); trigger.setAttribute('aria-expanded', 'false');
        const label = this.querySelector(`label[for="${select.id}"]`);
        if (label) label.htmlFor = trigger.id;
        const update = () => { trigger.textContent = select.selectedOptions[0]?.textContent.trim() || ''; trigger.setAttribute('aria-label', `${label?.textContent.trim() || 'Choose variant'}: ${trigger.textContent}`); };
        update(); select.addEventListener('change', update);
        select.hidden = true; select.after(trigger);
        this.cardPicker = { select, trigger, update };
        trigger.addEventListener('click', () => this.cardPicker.open ? this.closeCardPicker(true) : this.openCardPicker());
        trigger.addEventListener('keydown', event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); this.openCardPicker(); } });
      }

      openCardPicker() {
        const state = this.cardPicker;
        if (!state || state.open) return;
        document.querySelectorAll('product-form').forEach(form => { if (form !== this) form.closeCardPicker?.(false); });
        if (!state.menu) {
          const menu = document.createElement('div'); menu.className = 'ls-card-picker-menu'; menu.id = `${state.select.id}-menu`;
          menu.setAttribute('role', 'listbox'); menu.setAttribute('aria-label', this.dataset.cardVariantLabel || 'Choose variant');
          state.nativePopover = typeof menu.showPopover === 'function';
          if (state.nativePopover) menu.setAttribute('popover', 'auto');
          state.trigger.setAttribute('aria-controls', menu.id);
          [...state.select.options].forEach(option => {
            const button = document.createElement('button'); button.type = 'button'; button.setAttribute('role', 'option');
            button.dataset.value = option.value; button.textContent = option.textContent.trim(); button.disabled = option.disabled;
            button.addEventListener('click', () => {
              if (button.disabled) return;
              const x = window.scrollX, y = window.scrollY;
              state.select.value = option.value; state.select.dispatchEvent(new Event('change', { bubbles: true }));
              this.closeCardPicker(true);
              if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
            });
            menu.append(button);
          });
          menu.addEventListener('keydown', event => {
            const buttons = [...menu.querySelectorAll('button:not(:disabled)')]; let index = buttons.indexOf(document.activeElement);
            if (event.key === 'Escape') { event.preventDefault(); this.closeCardPicker(true); return; }
            if (event.key === 'Tab') { this.closeCardPicker(true); return; }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
            buttons[index]?.focus({ preventScroll: true });
            if (buttons[index]) menu.scrollTop = Math.max(0, buttons[index].offsetTop - menu.clientHeight / 2);
          });
          menu.addEventListener('toggle', event => { if (event.newState === 'closed' && state.open) this.closeCardPicker(false); });
          document.body.append(menu); state.menu = menu;
        }
        state.menu.querySelectorAll('button').forEach(button => button.setAttribute('aria-selected', String(button.dataset.value === state.select.value)));
        state.open = true; state.trigger.setAttribute('aria-expanded', 'true'); state.menu.classList.add('is-open');
        if (state.nativePopover) state.menu.showPopover();
        this.positionCardPicker();
        const selected = state.menu.querySelector('[aria-selected="true"]:not(:disabled)') || state.menu.querySelector('button:not(:disabled)');
        if (selected) { state.menu.scrollTop = Math.max(0, selected.offsetTop - state.menu.clientHeight / 2); selected.focus({ preventScroll: true }); }
        state.events = new AbortController();
        const schedule = event => {
          if (state.menu.contains(event.target) || state.frame) return;
          state.frame = requestAnimationFrame(() => { state.frame = null; this.positionCardPicker(); });
        };
        document.addEventListener('scroll', schedule, { capture: true, passive: true, signal: state.events.signal });
        window.addEventListener('resize', schedule, { signal: state.events.signal });
        window.visualViewport?.addEventListener('resize', schedule, { signal: state.events.signal });
        window.visualViewport?.addEventListener('scroll', schedule, { signal: state.events.signal });
        document.addEventListener('pointerdown', event => { if (!state.menu.contains(event.target) && !state.trigger.contains(event.target)) this.closeCardPicker(false); }, { signal: state.events.signal });
      }

      positionCardPicker() {
        const state = this.cardPicker;
        if (!state?.open) return;
        const r = state.trigger.getBoundingClientRect();
        const view = window.visualViewport;
        const leftEdge = view?.offsetLeft || 0, topEdge = view?.offsetTop || 0;
        const viewWidth = view?.width || window.innerWidth, viewHeight = view?.height || window.innerHeight;
        if (r.bottom <= topEdge || r.top >= topEdge + viewHeight || !state.trigger.isConnected) { this.closeCardPicker(false); return; }
        const width = Math.min(Math.max(r.width, 220), viewWidth - 16);
        const x = Math.max(leftEdge + 8, Math.min(r.left, leftEdge + viewWidth - width - 8));
        const below = topEdge + viewHeight - r.bottom - 14, above = r.top - topEdge - 14;
        const up = below < 180 && above > below;
        const maxHeight = Math.max(40, Math.min(240, up ? above : below));
        Object.assign(state.menu.style, { width: `${width}px`, maxHeight: `${maxHeight}px`, left: `${x}px`, top: '0px' });
        const height = state.menu.getBoundingClientRect().height;
        state.menu.style.top = `${up ? r.top - height - 6 : r.bottom + 6}px`;
        state.menu.dataset.placement = up ? 'above' : 'below';
      }

      closeCardPicker(restoreFocus = false) {
        const state = this.cardPicker;
        if (!state?.open) return;
        state.open = false; state.events?.abort();
        if (state.frame) cancelAnimationFrame(state.frame);
        state.frame = null;
        if (state.nativePopover && state.menu.matches(':popover-open')) state.menu.hidePopover();
        state.menu.classList.remove('is-open'); state.trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus && state.trigger.isConnected) state.trigger.focus({ preventScroll: true });
      }

      disconnectedCallback() {
        this.closeCardPicker(false);
        this.cardPicker?.menu?.remove();
        if (this.cardPicker) this.cardPicker.menu = null;
      }

      syncCardVariant() {
        const select = this.querySelector('[data-ls-card-variant]');
        const option = select?.selectedOptions[0];
        const card = this.closest('.product-card-wrapper');
        if (!option || !card) return;
        const available = option.dataset.available === 'true';
        this.toggleSubmitButton(!available, available ? this.dataset.cardAddLabel : this.dataset.cardSoldOutLabel);
        const quantity = this.form.querySelector('[name="quantity"]');
        if (quantity) quantity.value = option.dataset.min || '1';
        const price = card.querySelector('.price');
        if (price) {
          price.classList.toggle('price--on-sale', Boolean(option.dataset.compare));
          price.classList.toggle('price--sold-out', !available);
          price.classList.remove('price--no-compare');
          price.querySelectorAll('.price__regular .price-item--regular:not(s), .price__sale .price-item--sale').forEach(el => { el.textContent = option.dataset.price; });
          price.querySelectorAll('.price__sale .price-item--regular').forEach(el => { el.textContent = option.dataset.compare || ''; });
        }
        const image = card.querySelector('.card__media img');
        if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; image.alt = option.dataset.alt || image.alt; }
        card.querySelectorAll('a[href]').forEach(link => {
          const url = new URL(link.href, location.origin);
          if (url.pathname === select.dataset.productUrl) { url.searchParams.set('variant', select.value); link.href = url.pathname + url.search + url.hash; }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.pendingCartReview) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const cartMutationRoot =
          this.closest('cart-drawer') || (this.cart?.tagName === 'CART-DRAWER' ? this.cart : null);
        if (
          cartMutationRoot?.getAttribute('aria-busy') === 'true' ||
          cartMutationRoot?.cartItemsMutationToken
        ) return;
        cartMutationRoot?.setAttribute('aria-busy', 'true');
        this.openedCartForRequest = Boolean(this.hasAttribute('data-ls-card-form') && this.cart?.tagName === 'CART-DRAWER');
        if (this.openedCartForRequest) {
          this.cart.setAttribute('data-opening-pending', 'true');
          this.cart.open(this.submitButton);
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        let addSucceeded = false;
        const handleAddError = async (e) => {
          console.error(e);
          const uncertain = e.code === 'CART_ADD_UNCERTAIN' || addSucceeded;
          if (uncertain && this.pendingCartReview) return;
          const message = uncertain ? window.cartStrings.add_uncertain : e.message || window.cartStrings.error;
          if (uncertain) this.hideErrors = false;
          this.handleErrorMessage(message);
          if (uncertain) {
            this.pendingCartReview = true;
            this.error = true;
            this.submitButton.disabled = true;
            const reviewLink = document.createElement('a');
            reviewLink.href = routes.cart_url;
            reviewLink.textContent = window.cartStrings.review_cart;
            reviewLink.className = 'link underlined-link';
            this.errorMessageWrapper?.append(' ', reviewLink);
            const cartItems = this.cart?.querySelector('cart-drawer-items');
            if (cartItems) {
              try {
                await cartItems.onCartUpdate({ throwOnError: true });
                this.cart.open();
              } catch (refreshError) {
                console.error(refreshError);
              }
            }
          }
          this.dispatchCartErrorEvent(message, e.code || 'SERVICE_UNAVAILABLE');
          if (!addSucceeded) linesUpdateDeferred?.reject(e);
        };

        window.LSCartRequest.add(`${routes.cart_add_url}`, config)
          .then(async ({ response: networkResponse, data: response }) => {
            addSucceeded = networkResponse.ok && !response.status;
            if (!networkResponse.ok || response.status) {
              const errorDescription = response.description || response.message || window.cartStrings.error;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || errorDescription,
                message: response.message,
              });
              this.handleErrorMessage(errorDescription);
              this.dispatchCartErrorEvent(errorDescription, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorDescription));

              return;
            } else if (!this.cart || (networkResponse.ok && response.items && !response.sections && this.cart.tagName !== 'CART-DRAWER')) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              await publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              });
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const usesCartDrawer = this.cart?.tagName === 'CART-DRAWER';
            const showUpdatedCart = () => {
              if (usesCartDrawer) {
                this.cart.open();
                return;
              }
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            };
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    Promise.resolve().then(showUpdatedCart).catch(handleAddError);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              showUpdatedCart();
            }
          })
          .catch(handleAddError)
          .finally(() => {
            if (this.openedCartForRequest) this.cart?.removeAttribute('data-opening-pending');
            if (!cartMutationRoot?.cartItemsMutationToken) {
              cartMutationRoot?.removeAttribute('aria-busy');
            }
            this.submitButton.classList.remove('loading');
            if (addSucceeded && !this.pendingCartReview && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.openedCartForRequest) {
          const status = this.cart?.querySelector('[data-ls-cart-add-error]');
          if (status) { status.textContent = errorMessage || ''; status.hidden = !errorMessage; }
        }
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.pendingCartReview) {
          this.submitButton.disabled = true;
          return;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : window.LSCartRequest
              .json(`${routes.cart_url}.json`, {
                method: 'GET',
                headers: { Accept: 'application/json' },
              })
              .then(({ response, data }) => (response.ok ? data : null));

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
